/**
 * Cache invalidation after a catalog refresh — shared by the synchronous keyed mutation
 * and by the background job's terminal effect, so both paths drop exactly the same caches.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { QueryClient } from '@tanstack/react-query';

import { smswiserKeys } from '@/hooks/smswiser/core/useSMSWiser';
import { smswiserCampaignWorkspaceKeys } from '@/hooks/smswiser-campaign-workspace/keys';
import { smswiserCatalogKeys } from '@/hooks/smswiser/catalog/smswiserCatalogKeys';

function invalidateCountryCaches(qc: QueryClient, countryKeys: string[]): void {
  for (const key of countryKeys) {
    qc.invalidateQueries({ queryKey: smswiserCatalogKeys.countryBundle(key) });
    qc.invalidateQueries({ queryKey: smswiserKeys.country(key) });
    qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.listsWithStatsPrefix(key) });
    qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.gatewaysWithStatsPrefix(key) });
    qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.dropsWithStatsPrefix(key) });
    qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.textsWithStatsPrefix(key) });
    qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.sourcesWithStatsPrefix(key) });
    qc.invalidateQueries({
      queryKey: [...smswiserCampaignWorkspaceKeys.all, 'pathsWithStats', key],
    });
    qc.invalidateQueries({
      queryKey: [...smswiserCampaignWorkspaceKeys.all, 'landersWithStats', key],
    });
    qc.invalidateQueries({
      queryKey: [...smswiserCampaignWorkspaceKeys.all, 'offersWithStats', key],
    });
  }
}

/**
 * @param keys refreshed country keys; omit (or empty) for a full refresh, which touched
 *   every country and therefore invalidates all country-dependent and stats caches.
 */
export function invalidateAfterCatalogRefresh(qc: QueryClient, keys?: string[]): void {
  if (keys?.length) {
    invalidateCountryCaches(qc, keys);
    qc.invalidateQueries({ queryKey: smswiserCatalogKeys.countries() });
    qc.invalidateQueries({ queryKey: smswiserCatalogKeys.countriesWithStatus() });
    return;
  }

  qc.invalidateQueries({ queryKey: smswiserCatalogKeys.all });
  qc.invalidateQueries({ queryKey: ['smswiser', 'countries'] });
  qc.invalidateQueries({ queryKey: smswiserCampaignWorkspaceKeys.all });
}
